import { useNavigate, useSearchParams } from "react-router-dom";
import { useRootStore } from "@/state/RootStore";
import DakotaThumbUpHero from "@/assets/images/dakota-thumb-up-hero.webp";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { APP_TO_BUILD } from "../../../appsConfig";
import { captureEvent } from "@/utils/posthog";
import { getPaymentSuccessCopy } from "@/i18n/paymentSuccessCopy";
import { getPurchaseConfirmDelay } from "@/utils/purchaseConfirmation";
import * as Sentry from "@sentry/react";

const PaymentSuccess = () => {
  const { t } = useTranslation();
  const store = useRootStore();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [isRefreshingPurchaseState, setIsRefreshingPurchaseState] = useState(true);
  // Purchase completed, but entitlements have not appeared yet — almost always the
  // Stripe webhook still propagating.
  const [isUnconfirmed, setIsUnconfirmed] = useState(false);

  const variant = params.get("variant");
  const planParam = params.get("plan");
  const plan: "monthly" | "annual" | undefined =
    planParam === "monthly" || planParam === "annual" ? planParam : undefined;
  const isAiPurchase = (APP_TO_BUILD === "electrical" || APP_TO_BUILD === "plumbing") && variant === "ai";

  const banner = useMemo(() => {
    const lookupKey = isAiPurchase ? `${APP_TO_BUILD}_ai` : APP_TO_BUILD;
    return getPaymentSuccessCopy(t, lookupKey);
  }, [t, isAiPurchase]);

  useEffect(() => {
    let cancelled = false;

    const hasPurchasedAccess = () =>
      isAiPurchase
        ? Boolean(store.mainAppStore.hasAiAccess())
        : Boolean(store.mainAppStore.hasStudyAccess(false));

    // POLLS, rather than checking once. The dominant failure here is not an error: while
    // the Stripe checkout webhook is still in flight, `check-purchase-state` answers
    // 200 OK with legitimately-FREE entitlements, so a single check (or the old 600ms
    // retry) routinely showed a paying customer the free tier. Only asking again fixes it.
    const confirmPurchase = async () => {
      for (let attempt = 0; ; attempt++) {
        const delay = getPurchaseConfirmDelay(attempt);
        if (delay === null) break;

        if (delay > 0) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
        if (cancelled) return;

        try {
          await store.mainAppStore.checkPurchaseState();
        } catch (error) {
          Sentry.captureException(error, {
            tags: { area: "purchase_success", phase: "confirm" },
          });
        }
        if (cancelled) return;

        if (hasPurchasedAccess()) {
          setIsRefreshingPurchaseState(false);
          captureEvent(
            isAiPurchase
              ? "web_ai_purchase_success_view"
              : "web_purchase_success_view",
            { ...(plan ? { plan } : {}), confirmed: true },
          );
          return;
        }
      }

      if (cancelled) return;

      // Ceiling reached and still no paid access. Say so instead of silently presenting
      // a success screen — but do NOT block the button: their purchase very likely did
      // succeed and is simply still propagating.
      setIsRefreshingPurchaseState(false);
      setIsUnconfirmed(true);
      // The success event used to fire unconditionally, so `web_purchase_success_view`
      // over-reported verified activations. Now it carries what actually happened.
      captureEvent(
        isAiPurchase
          ? "web_ai_purchase_success_view"
          : "web_purchase_success_view",
        { ...(plan ? { plan } : {}), confirmed: false },
      );
    };

    confirmPurchase();

    return () => {
      cancelled = true;
    };
  }, [isAiPurchase, plan, store.mainAppStore]);

  const handleExplore = useCallback(() => {
    navigate(
      store.mainAppStore.profilePersistedComplete ? "/study" : "/create-profile",
      { replace: true },
    );
  }, [navigate, store.mainAppStore]);

  return (
    <div className="size-full flex justify-center gap-10 pt-10">
      <div className="flex flex-col items-center justify-center gap-4">
        <img
          className="size-[534px]"
          src={DakotaThumbUpHero}
          alt={t("paymentSuccess.hero_alt")}
        />
      </div>

      <div className="flex flex-col max-w-[650px] gap-12 justify-center">
        <div className="flex flex-col gap-2">
          <h1 className="font-extrabold text-5xl leading-[100%] text-[#0F172A]">
            {banner.header}
          </h1>
        </div>
        <p className="font-medium text-2xl leading-[31px]">
          {banner.description}
        </p>
        {isUnconfirmed && (
          <p role="status" className="text-base leading-6 text-[#8A6D3B]">
            {t("paymentSuccess.still_processing")}
          </p>
        )}
        <button
          onClick={handleExplore}
          disabled={isRefreshingPurchaseState}
          className="w-full py-4 disabled:bg-gray-100 bg-primary text-white rounded-full disabled:text-gray-500 font-medium"
        >
          {isRefreshingPurchaseState
            ? t("paymentSuccess.updating_access")
            : t("paymentSuccess.explore_now")}
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccess;
